import { useRef, useEffect, useCallback, type MutableRefObject } from 'react';
import { TextSelection } from '@tiptap/pm/state';
import { buildEditorContent, convertTextPatternsToTags, convertTagsToText } from '../tiptapEditorUtils';
import { getTagDisplay } from '../utils';
import { VARIABLE_TAG_NAME, MENTION_TAG_NAME } from '../constants';
import type { AvailableInput } from '../types';
import type { Editor } from '@tiptap/react';
import type { PromptDisplayMode } from 'web';

interface UseEditorUpdatesExternallyProps {
  editor: Editor | null;
  value: string;
  availableInputs: AvailableInput[];
  displayMode: PromptDisplayMode;
  hasEditingPermissions: boolean;
  lastValueRef: MutableRefObject<string>;
}

export const useEditorUpdatesExternally = ({
  editor,
  value,
  availableInputs,
  displayMode,
  hasEditingPermissions,
  lastValueRef,
}: UseEditorUpdatesExternallyProps) => {
  const prevDisplayModeRef = useRef(displayMode);
  const prevAvailableInputsRef = useRef(availableInputs);

  /**
   * Diff the editor's current document against a new value and apply only the
   * minimal replacement. This avoids a full `setContent` rebuild and naturally
   * positions the cursor at the end of the changed region (useful for undo/redo).
   */
  const updateEditorContent = useCallback(() => {
    if (!editor) return;

    const oldDoc = editor.state.doc;
    // Build the new document from the updated value
    const newDoc = editor.schema.nodeFromJSON({
      type: 'doc',
      content: buildEditorContent(value, availableInputs, displayMode),
    });

    // Diff old and new documents at the ProseMirror level.
    const diffStart = oldDoc.content.findDiffStart(newDoc.content);
    const hasStartDiff = diffStart !== null;
    const diffEnd = hasStartDiff && oldDoc.content.findDiffEnd(newDoc.content);

    if (hasStartDiff && diffEnd) {
      let { a: endA, b: endB } = diffEnd;

      // When identical nodes repeat (e.g. the same tag multiple times),
      // findDiffStart and findDiffEnd can match the same node in both the
      // common prefix and suffix, causing them to overlap. Shift the suffix
      // boundaries forward by the overlap amount to produce valid ranges.
      const overlap = diffStart - Math.min(endA, endB);
      if (overlap > 0) {
        endA += overlap;
        endB += overlap;
      }

      // Extract the replacement slice from the new document and apply it.
      const slice = newDoc.slice(diffStart, endB);
      const { tr } = editor.state;
      tr.replace(diffStart, endA, slice);

      // Place cursor at the end of the inserted content
      const cursorPos = tr.mapping.map(endA);
      const resolvedPos = tr.doc.resolve(cursorPos);
      tr.setSelection(TextSelection.near(resolvedPos));

      editor.view.dispatch(tr);
    }
  }, [editor, value, availableInputs, displayMode]);

  /**
   * Update all tags in the editor to reflect the new display mode.
   * Also handles added inputs (convert text patterns → tags) and
   * removed inputs (convert orphaned tags → text).
   */
  const updateAllTagsDisplayMode = useCallback(
    ({
      shouldFocus = false,
      addedInputs = [],
      removedKeys = new Set<string>(),
    }: {
      shouldFocus?: boolean;
      addedInputs?: AvailableInput[];
      removedKeys?: Set<string>;
    } = {}) => {
      if (!editor) return;

      // 1. Convert text patterns → tags for newly added inputs
      if (addedInputs.length > 0) {
        convertTextPatternsToTags(editor, addedInputs, displayMode);
      }

      // 2. Convert orphaned tags → text for removed inputs
      if (removedKeys.size > 0) {
        convertTagsToText(editor, removedKeys);
      }

      // 3. Update display attributes on all remaining tags
      const { tr } = editor.state;
      let modified = false;

      // Create a map of inputs by key for quick lookup
      const inputsByKey = new Map(availableInputs.map((input) => [input.key, input]));

      // Traverse all nodes in the document
      editor.state.doc.descendants((node, pos) => {
        // Only process variable tags (not mention triggers)
        if (node.type.name === VARIABLE_TAG_NAME && node.attrs.tagKey !== MENTION_TAG_NAME) {
          const tagKey = node.attrs.tagKey as string;
          const inputInfo = inputsByKey.get(tagKey);

          if (inputInfo) {
            // Update the node's attributes with new display properties
            tr.setNodeMarkup(pos, undefined, {
              tagKey,
              ...getTagDisplay(inputInfo, displayMode),
            });
            modified = true;
          }
        }
        return true;
      });

      // Dispatch the transaction if any tags were modified
      if (modified) {
        editor.view.dispatch(tr);
        if (shouldFocus) {
          editor.view.focus();
        }
      }
    },
    [editor, availableInputs, displayMode],
  );

  const handleValueChanged = useCallback(() => {
    const valueChanged = value !== lastValueRef.current;
    if (editor && valueChanged) {
      lastValueRef.current = value;
      updateEditorContent();
    }
  }, [value, lastValueRef, editor, updateEditorContent]);

  const handleDisplayModeChanged = useCallback(() => {
    const displayModeChanged = displayMode !== prevDisplayModeRef.current;
    if (editor && displayModeChanged) {
      prevDisplayModeRef.current = displayMode;
      updateAllTagsDisplayMode({ shouldFocus: true });
    }
  }, [displayMode, prevDisplayModeRef, editor, updateAllTagsDisplayMode]);

  const handleAvailableInputsChanged = useCallback(() => {
    const prevInputs = prevAvailableInputsRef.current;
    const prevInputsByKey = new Map(prevInputs.map((i) => [i.key, i]));
    const currentInputsByKey = new Map(availableInputs.map((i) => [i.key, i]));

    // Detect added inputs (present in current but not in previous)
    const addedInputs = availableInputs.filter((input) => !prevInputsByKey.has(input.key));

    // Detect removed inputs (present in previous but not in current)
    const removedKeys = new Set(prevInputs.filter((input) => !currentInputsByKey.has(input.key)).map((i) => i.key));

    // Detect changed inputs (same key, different properties)
    const inputsChanged = availableInputs.some((input) => {
      const prevInput = prevInputsByKey.get(input.key);
      if (!prevInput) return false;
      return (
        prevInput.label !== input.label || prevInput.value !== input.value || prevInput.isIterator !== input.isIterator
      );
    });

    if (editor && (addedInputs.length > 0 || removedKeys.size > 0 || inputsChanged)) {
      updateAllTagsDisplayMode({ addedInputs, removedKeys });
    }
    prevAvailableInputsRef.current = availableInputs;
  }, [availableInputs, prevAvailableInputsRef, editor, updateAllTagsDisplayMode]);

  // Handle value changes
  useEffect(() => {
    if (!editor) return;

    handleValueChanged();
  }, [editor, handleValueChanged]);

  // Handle displayMode changes separately - update tags without rebuilding content
  useEffect(() => {
    if (!editor) return;

    handleDisplayModeChanged();
  }, [editor, handleDisplayModeChanged]);

  // Handle availableInputs changes separately - update tags without rebuilding content
  useEffect(() => {
    if (!editor) return;

    handleAvailableInputsChanged();
  }, [editor, handleAvailableInputsChanged]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(hasEditingPermissions);
    }
  }, [editor, hasEditingPermissions]);
};
